import { call, fork, put, select, take, takeEvery } from "typed-redux-saga";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { callApi } from "../sagas";
import { getApiErrorMessage } from "../../util/errors";
import { hasBlockingFormValidationErrors } from "../global-types/store-errors";
import { SaveResult, buildSaveFinishResult } from "../../web-apis/save-response";
import { allModelCustomizationsReset, customizationBaseDuplicated, customizationDuplicationItemsAdded, customizationOutputReplaced, imageSelectors, modelCustomizationExported, modelCustomizationExportFailed, modelCustomizationImported, modelCustomizationImportFailed, modelCustomizationObjectsSetAsModified, modelCustomizationPdfExportRequested, modelCustomizationSaved, modelCustomizationsSet, pdfExportFinished, pdfExportStarted, resetAllModelCustomizationsFinished, resetAllModelCustomizationsStarted, resetSingleOutputFinished, resetSingleOutputStarted, saveModelCustomizationFinished, saveModelCustomizationStarted, singleCustomizationReset } from "./imageSlice";
import { Action } from "@reduxjs/toolkit";
import { BodyMask, EXPORT_IMAGE_JSON_FILE_PREFIX, FillHoles, HoleMask, ImageContourGeneration, ImageCustomizationEntities, ImageCustomizationEntitiesForExport, ImageCustomizationOutput, ImageDicomRestriction, ImageDicomTag, ImageOutputGeometry, ImagePostProcessing, KeepLargestComponent } from "./image-types";
import { createNewModelCustomizationBase, duplicateModelCustomizationMetadataItem, generateNewId } from "../global-types/customization-helpers";
import { DuplicatedIdMap } from "../global-types/reducer-helpers";
import { METADATA_FILENAME_ATTRIBUTE, ModelType, OutputMetadataItem } from "../global-types/customization-types";
import { ensureFilenameHasDcmExtension } from "../../pages/customization/file-operations";
import { convertImageViewModelsToJson, createNewImageCustomizationOutput, duplicateBodyMask, duplicateContourGeneration, duplicateDicomRestriction, duplicateDicomTag, duplicateFillHoles, duplicateHoleMask, duplicateKeepLargestComponent, duplicateOutputGeometry, duplicatePostProcessing } from "./image-helpers";
import { modelCustomizationPageFocusSet } from "../appStatus/appStatusSlice";
import { appConfigSelectors } from "../appConfig/appConfigSlice";
import { DeploymentMode } from "../appConfig/appDeploymentInfoTypes";
import { downloadBlobToDisk, downloadFileToDisk } from "../../util/files";

/**
 * Saga to fetch image customizations using the provided application client.
 *
 * This saga function performs an API call to retrieve image customizations
 * and handles the success or failure of the operation. On success, it updates
 * the store with the fetched customizations. On failure, it updates the store
 * with an error message.
 * 
 * @param appClient - An instance of `MVisionAppClient` or `undefined`. If `undefined`,
 *                    the saga will return `null` for image customizations.
 * 
 * @returns The result of the API call, which is either the fetched image customizations
 *          or `null` if the operation fails or the `appClient` is `undefined`.
 */
export function* fetchImageCustomizationsSaga(appClient: MVisionAppClient | undefined) {

    const result = yield* callApi({
        doApiCall: function* (client) {
            const imageCustomizations = appClient === undefined ? null : yield* call(async () => client.fetchImageCustomizationsAsync(appClient));
            if (imageCustomizations !== null) {
                const fetchModels = yield* call(async () => client.fetchAvailableModelsAsync(appClient, ModelType.Image));
                if (fetchModels !== undefined) {
                    // if the available models collection is in use, mark which models are unavailable
                    for (const model of imageCustomizations.models) {
                        model.isAvailable = fetchModels.availableModels.includes(model.modelName);
                    }
                } else {
                    throw new Error('Could not retrieve list of available Image+ models');
                }
            }
            return imageCustomizations;
        },
        onSuccess: function* (result) {
            yield* put(modelCustomizationsSet({ customizations: result }));
        },
        onFailure: function* (error) {
            yield* put(modelCustomizationsSet({ customizations: null, errorMessage: error.message || 'Unspecified error.' }));
        },
    });

    return result;
}

function* saveImageCustomizationSaga(action: Action) {
    if (modelCustomizationSaved.match(action)) {
        const { configurationTarget, force } = action.payload;


        yield* callApi({
            doApiCall: function* (client) {
                // collect model customization -- this is essentially a cache database dump
                const modelCustomization: ImageCustomizationEntitiesForExport = yield* collectImageCustomizationForExport();

                // sanity-check that everything is valid in case invalid data slips through the UI debouncing
                const formValidationErrors = yield* select(imageSelectors.selectFormValidationErrors);
                if (hasBlockingFormValidationErrors(formValidationErrors)) {
                    throw new Error(`Image+ model configuration has invalid entries. Please fix them before saving.`);
                }

                yield* put(saveModelCustomizationStarted());
                const result = yield* call(async () => client.saveImageCustomizationAsync(configurationTarget, modelCustomization, force));
                return result;
            },
            onFinish: function* (result: SaveResult | undefined, error) {
                const r = buildSaveFinishResult(result, error, ModelType.Image);
                yield* put(saveModelCustomizationFinished({
                    saveWasSuccessful: r.success,
                    errorMessage: r.errorMessage,
                    error: r.validationError,
                    concurrentSaveError: r.concurrentSaveError,
                    updatedTimestamps: r.updatedTimestamps,
                }));
                return r.success;
            }
        });
    }
}

function* duplicateImageCustomizationSaga(action: Action) {
    if (customizationBaseDuplicated.match(action)) {
        const { customizationBaseId, newCustomizationName } = action.payload;

        const sourceCustomizationBase = yield* select(imageSelectors.selectCustomizationBaseById, customizationBaseId);
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${customizationBaseId}`); }
        const parentSegmentationModel = yield* select(imageSelectors.selectModelById, sourceCustomizationBase.modelId);
        if (!parentSegmentationModel) { throw new Error(`Could not retrieve parent model with id ${sourceCustomizationBase.modelId}`); }

        const newCustomizationBaseId = generateNewId();

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: customizationBaseId, targetId: newCustomizationBaseId }];

        // duplicate customization outputs (note: triggers are NOT duplicated)
        const newOutputs: ImageCustomizationOutput[] = [];
        let newMetadata: OutputMetadataItem[] = [];
        const newDicomRestrictions: ImageDicomRestriction[] = [];
        let newDicomTags: ImageDicomTag[] = [];
        const newOutputGeometry: ImageOutputGeometry[] = [];
        const newContourGeneration: ImageContourGeneration[] = [];
        const newPostProcessing: ImagePostProcessing[] = [];
        const newKeepLargestComponent: KeepLargestComponent[] = [];
        const newFillHoles: FillHoles[] = [];
        const newBodyMasks: BodyMask[] = [];
        const newHoleMasks: HoleMask[] = [];

        for (const outputId of sourceCustomizationBase.outputs) {
            const sourceCustomizationOutput = yield* select(imageSelectors.selectOutputById, outputId);
            if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${outputId}`); }
            const duplicationEntities = yield* duplicateCustomizationOutputFromStore(sourceCustomizationOutput, undefined, newCustomizationBaseId);

            newOutputs.push(duplicationEntities.duplicatedOutput);
            newMetadata = newMetadata.concat(duplicationEntities.newMetadata);
            newDicomRestrictions.push(duplicationEntities.newDicomRestriction);
            newDicomTags = newDicomTags.concat(duplicationEntities.newDicomTags);
            newOutputGeometry.push(duplicationEntities.newOutputGeometry);
            newContourGeneration.push(duplicationEntities.newContourGeneration);
            newPostProcessing.push(duplicationEntities.newPostProcessing);
            newKeepLargestComponent.push(duplicationEntities.newKeepLargestComponent);
            newFillHoles.push(duplicationEntities.newFillHoles);
            newBodyMasks.push(duplicationEntities.newBodyMasks);
            newHoleMasks.push(duplicationEntities.newHoleMasks);

            duplicatedIds.push(...duplicationEntities.duplicatedIds);
        }

        // duplicate customization base
        const duplicatedCustomizationBase = createNewModelCustomizationBase(
            newCustomizationName,
            sourceCustomizationBase.description,
            newOutputs.map(o => o.id),
            [],
            [],
            sourceCustomizationBase.modelId,
            newCustomizationBaseId,
            true
        );

        // store everything
        yield* put(customizationDuplicationItemsAdded({
            parentModelId: parentSegmentationModel.id,
            customization: duplicatedCustomizationBase,
            outputs: newOutputs,
            metadata: newMetadata,

            imageDicomRestrictions: newDicomRestrictions,
            imageDicomTags: newDicomTags,
            imageOutputGeometry: newOutputGeometry,
            imageContourGeneration: newContourGeneration,
            imagePostProcessing: newPostProcessing,
            keepLargestComponent: newKeepLargestComponent,
            fillHoles: newFillHoles,
            bodyMasks: newBodyMasks,
            holeMasks: newHoleMasks,

            duplicatedIds: duplicatedIds,
        }));

        // focus on the new duplicate
        if (newOutputs.length > 0) {
            yield* put(modelCustomizationPageFocusSet(newOutputs[0].id));
        }
    }
}

function* resetAllImageCustomizationsSaga(action: Action) {
    if (allModelCustomizationsReset.match(action)) {
        const configurationTarget = action.payload;

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetAllModelCustomizationsStarted());

                let success = yield* call(async () => client.deleteImageCustomizationAsync(configurationTarget));
                if (success) {
                    // immediately fetch updated (reset) customizations
                    success = yield* call(fetchImageCustomizationsSaga, configurationTarget);
                }

                return success;
            },
            onFinish: function* (result, error) {
                let errorMessage = null;
                const success = result || false;

                if (error !== null) {
                    console.error('Was not able to reset Image+ customizations');
                    errorMessage = getApiErrorMessage(error);
                    console.error(errorMessage);
                } else if (!success) {
                    errorMessage = 'Unable to reset Image+ customizations. There might be a problem with the server.';
                }


                yield* put(resetAllModelCustomizationsFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            },
        });
    }
}

/** Resets a single Image+ CUSTOMIZATION BASE (not output) */
function* resetSingleImageCustomizationSaga(action: Action) {
    if (singleCustomizationReset.match(action)) {

        // NOTE: although we're technically resetting an entire customization base here, in practice
        // this code only resets a single customization output. Currently Image+ does not support
        // having multiple output files per customization so resetting just the single default output
        // file is fine. This approach must be revised if we ever support adding multiple output
        // files per customization for Image+.
        const customizationId = action.payload;

        const customization = yield* select(imageSelectors.selectCustomizationBaseById, customizationId);

        if (!customization) { throw new Error('Could not find correct customization for reset'); }
        if (customization.outputs.length !== 1) { throw new Error('Expected customization to have exactly one output -- cannot reset'); }

        const output = yield* select(imageSelectors.selectOutputById, customization.outputs[0]);
        if (!output) { throw new Error('Could not find correct customization output for reset'); }

        const customizationOutputId = output.id;
        const modelName = yield* select(imageSelectors.selectModelNameForOutput, customizationOutputId);
        if (!modelName) { throw new Error('No model name specified, cannot fetch default configuration for model'); }

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetSingleOutputStarted());

                // get default config for this model
                return yield* call(async () => client.fetchDefaultConfigurationForImageOutput(modelName));
            },
            onFinish: function* (result, error) {
                if (result) {
                    // replace existing output and its items with received default items
                    yield* put(customizationOutputReplaced({ customizationOutputId, newOutputEntities: result }));
                }

                let errorMessage = null;
                if (error !== null) {
                    console.error(`Was not able to reset single customization output (${customizationOutputId})`);
                    errorMessage = `An error occurred when trying to reset target file: ${getApiErrorMessage(error)}`;
                    console.error(errorMessage);
                }

                const success = error === null;
                yield* put(resetSingleOutputFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            }
        });
    }
}

function* exportImageCustomization(appClient: MVisionAppClient | undefined) {
    try {
        // collect model customization -- this is essentially a cache database dump
        const modelCustomization: ImageCustomizationEntitiesForExport = yield* collectImageCustomizationForExport();
        const jsonObject = yield* call(async () => convertImageViewModelsToJson(modelCustomization, true));

        // Default filename if appClient?.userName is undefined or empty
        const defaultFilename = "model-customizations.json";
        const localModeFilename = "local.json";
        const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
        const filename = `${EXPORT_IMAGE_JSON_FILE_PREFIX}${isLocalMode ? localModeFilename : (appClient?.userName ? `${appClient.userName}.json` : defaultFilename)}`;

        downloadFileToDisk(JSON.stringify(jsonObject, null, 2), filename, 'application/json');
        yield* put(modelCustomizationExportFailed(null)); // resets any failure error message
    } catch (error) {
        console.log(`An error occurred in exportImageCustomization:`);
        console.error(error);

        const errorMessage = getApiErrorMessage(error, 'Image+ JSON export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
    }
}

function* importImageCustomization(modelCustomization: ImageCustomizationEntities) {
    try {
        yield* put(modelCustomizationsSet({ customizations: modelCustomization, retainOriginalData: true }));
        yield* put(modelCustomizationObjectsSetAsModified(modelCustomization)); // set the entire ModelCustomizationEntities object as modified
        yield* put(modelCustomizationImportFailed(null));  // resets any failure error message
    } catch (error) {
        const errorMessage = getApiErrorMessage(error, 'Image+ JSON import failed.');
        yield* put(modelCustomizationImportFailed(errorMessage));
    }
}



/// helper functions for image config sagas

export function* collectImageCustomizationForExport() {
    const modelCustomization: ImageCustomizationEntitiesForExport = {
        models: yield* select(imageSelectors.selectModels),
        customizationBases: yield* select(imageSelectors.selectCustomizationBases),
        imageOutputs: yield* select(imageSelectors.selectOutputs),
        outputMetadata: yield* select(imageSelectors.selectOutputMetadata),

        imageDicomRestrictions: yield* select(imageSelectors.selectDicomRestrictions),
        imageDicomTags: yield* select(imageSelectors.selectDicomTags),
        imageOutputGeometry: yield* select(imageSelectors.selectImageOutputGeometry),
        imageContourGeneration: yield* select(imageSelectors.selectImageContourGeneration),
        imagePostProcessing: yield* select(imageSelectors.selectImagePostProcessing),
        keepLargestComponent: yield* select(imageSelectors.selectKeepLargestComponent),
        fillHoles: yield* select(imageSelectors.selectFillHoles),
        bodyMasks: yield* select(imageSelectors.selectBodyMasks),
        holeMasks: yield* select(imageSelectors.selectHoleMasks),

        aeTitleRules: yield* select(imageSelectors.selectAeTitleRules),
        dicomRules: yield* select(imageSelectors.selectDicomRules),
        dicomAttributeRules: yield* select(imageSelectors.selectDicomAttributeRules),
    };

    return modelCustomization;
}

type DuplicatedOutputEntities = {
    duplicatedOutput: ImageCustomizationOutput;
    newMetadata: OutputMetadataItem[];

    newDicomRestriction: ImageDicomRestriction;
    newDicomTags: ImageDicomTag[];
    newOutputGeometry: ImageOutputGeometry;
    newContourGeneration: ImageContourGeneration;
    newPostProcessing: ImagePostProcessing;
    newKeepLargestComponent: KeepLargestComponent;
    newFillHoles: FillHoles;
    newBodyMasks: BodyMask;
    newHoleMasks: HoleMask;

    duplicatedIds: DuplicatedIdMap[];
};

/**
 * Duplicates a customization output from store.
 * 
 * This should only be called from within related sagas (or from unit tests).
 * 
 */
function* duplicateCustomizationOutputFromStore(
    sourceCustomizationOutput: ImageCustomizationOutput,
    newFilename?: string,
    newCustomizationBaseId?: string) {

    const newDicomTags: ImageDicomTag[] = [];
    const newMetadata: OutputMetadataItem[] = [];
    const newCustomizationOutputId = generateNewId();
    const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: sourceCustomizationOutput.id, targetId: newCustomizationOutputId }];

    let duplicateFilename: string = '';

    // duplicate metadata
    for (const metadataId of sourceCustomizationOutput.metadata) {
        const metadata = yield* select(imageSelectors.selectOutputMetadataById, metadataId);
        if (!metadata) { throw new Error(`Could not find customization metadata with id ${metadataId}`) }
        const duplicatedMetadata = duplicateModelCustomizationMetadataItem(metadata, newCustomizationOutputId);

        // handle filename metadata
        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE && newFilename !== undefined) {
            duplicatedMetadata.value = ensureFilenameHasDcmExtension(newFilename);
        }

        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE) {
            duplicateFilename = duplicatedMetadata.value;
        }

        newMetadata.push(duplicatedMetadata);
        duplicatedIds.push({ sourceId: metadataId, targetId: duplicatedMetadata.id });
    }

    // get main source objects
    const dicomRestriction = yield* select(imageSelectors.selectDicomRestrictionById, sourceCustomizationOutput.dicomRestriction);
    const outputGeometry = yield* select(imageSelectors.selectImageOutputGeometryById, sourceCustomizationOutput.outputGeometry);
    const contourGeneration = yield* select(imageSelectors.selectImageContourGenerationById, sourceCustomizationOutput.contourGeneration);
    const postProcessing = yield* select(imageSelectors.selectImagePostProcessingById, sourceCustomizationOutput.postProcessing);
    if (!dicomRestriction) { throw new Error(`Could not find dicom restriction with id ${sourceCustomizationOutput.dicomRestriction}`); }
    if (!outputGeometry) { throw new Error(`Could not find output geometry with id ${sourceCustomizationOutput.outputGeometry}`); }
    if (!contourGeneration) { throw new Error(`Could not find contour generation with id ${sourceCustomizationOutput.contourGeneration}`); }
    if (!postProcessing) { throw new Error(`Could not find post processing with id ${sourceCustomizationOutput.postProcessing}`); }

    // get post-processing objects
    const keepLargestComponent = yield* select(imageSelectors.selectKeepLargestComponentById, postProcessing.keepLargestComponent);
    const fillHoles = yield* select(imageSelectors.selectFillHolesById, postProcessing.fillHoles);
    if (!keepLargestComponent) { throw new Error(`Could not find keep largest component object with id ${postProcessing.keepLargestComponent}`); }
    if (!fillHoles) { throw new Error(`Could not find fill holes object with id ${postProcessing.fillHoles}`); }
    const bodyMask = yield* select(imageSelectors.selectBodyMaskById, fillHoles.bodyMask);
    const holeMask = yield* select(imageSelectors.selectHoleMaskById, fillHoles.holeMask);
    if (!bodyMask) { throw new Error(`Could not find body mask with id ${fillHoles.bodyMask}`); }
    if (!holeMask) { throw new Error(`Could not find hole mask with id ${fillHoles.holeMask}`); }

    // generate some ids we'll be using here
    const duplicatedDicomRestrictionId = generateNewId();
    const duplicatedOutputGeometryId = generateNewId();
    const duplicatedContourGenerationId = generateNewId();
    const duplicatedPostProcessingId = generateNewId();
    const duplicatedKeepLargestComponentId = generateNewId();
    const duplicatedFillHolesId = generateNewId();
    const duplicatedBodyMaskId = generateNewId();
    const duplicatedHoleMaskId = generateNewId();

    // duplicate dicom tags
    for (const dicomTagId of dicomRestriction.tags) {
        const dicomTag = yield* select(imageSelectors.selectDicomTagById, dicomTagId);
        if (!dicomTag) { throw new Error(`Could not find dicom tag with id ${dicomTagId}`); }
        const duplicatedDicomTag = duplicateDicomTag(dicomTag, duplicatedDicomRestrictionId);
        newDicomTags.push(duplicatedDicomTag);
        duplicatedIds.push({ sourceId: dicomTagId, targetId: duplicatedDicomTag.id });
    }

    // duplicate post-processing objects
    const duplicatedBodyMask = duplicateBodyMask(bodyMask, duplicatedFillHolesId, duplicatedBodyMaskId);
    const duplicatedHoleMask = duplicateHoleMask(holeMask, duplicatedFillHolesId, duplicatedHoleMaskId);
    const duplicatedFillHoles = duplicateFillHoles(fillHoles, duplicatedBodyMask, duplicatedHoleMask, duplicatedPostProcessingId, duplicatedFillHolesId);
    const duplicatedKeepLargestComponent = duplicateKeepLargestComponent(keepLargestComponent, duplicatedPostProcessingId, duplicatedKeepLargestComponentId);
    duplicatedIds.push(
        { sourceId: bodyMask.id, targetId: duplicatedBodyMask.id },
        { sourceId: holeMask.id, targetId: duplicatedHoleMask.id },
        { sourceId: fillHoles.id, targetId: duplicatedFillHoles.id },
        { sourceId: keepLargestComponent.id, targetId: duplicatedKeepLargestComponent.id }
    );

    // duplicate output main objects
    const duplicatedDicomRestriction = duplicateDicomRestriction(dicomRestriction, newDicomTags.map(d => d.id), newCustomizationOutputId, duplicatedDicomRestrictionId);
    const duplicatedOutputGeometry = duplicateOutputGeometry(outputGeometry, newCustomizationOutputId, duplicatedOutputGeometryId);
    const duplicatedContourGeneration = duplicateContourGeneration(contourGeneration, newCustomizationOutputId, duplicatedContourGenerationId);
    const duplicatedPostProcessing = duplicatePostProcessing(postProcessing, duplicatedKeepLargestComponent, duplicatedFillHoles, newCustomizationOutputId, duplicatedPostProcessingId);
    duplicatedIds.push(
        { sourceId: dicomRestriction.id, targetId: duplicatedDicomRestriction.id },
        { sourceId: outputGeometry.id, targetId: duplicatedOutputGeometry.id },
        { sourceId: contourGeneration.id, targetId: duplicatedContourGeneration.id },
        { sourceId: postProcessing.id, targetId: duplicatedPostProcessing.id }
    );


    // create new output
    const metadataIds = newMetadata.map(m => m.id);
    const duplicatedOutput = createNewImageCustomizationOutput(
        metadataIds,
        duplicatedDicomRestrictionId,
        duplicatedOutputGeometryId,
        duplicatedContourGenerationId,
        duplicatedPostProcessingId,
        newCustomizationBaseId || sourceCustomizationOutput.modelCustomizationBaseId,
        duplicateFilename,
        newCustomizationOutputId,
        true
    );

    const result: DuplicatedOutputEntities = {
        duplicatedOutput,
        newMetadata,
        newDicomRestriction: duplicatedDicomRestriction,
        newDicomTags,
        newOutputGeometry: duplicatedOutputGeometry,
        newContourGeneration: duplicatedContourGeneration,
        newPostProcessing: duplicatedPostProcessing,
        newKeepLargestComponent: duplicatedKeepLargestComponent,
        newFillHoles: duplicatedFillHoles,
        newBodyMasks: duplicatedBodyMask,
        newHoleMasks: duplicatedHoleMask,
        duplicatedIds,
    };

    return result;
};



/// watches for image config sagas

export function* watchSaveImageCustomizationSaga() {
    // take all image customization save requests as they come -- do limiting simultaenous saves by temporarily
    // disabling relevant ui elements instead of doing it with sagas here
    yield* takeEvery(modelCustomizationSaved, saveImageCustomizationSaga);
}

export function* watchImageCustomizationDuplicatedSaga() {
    yield* takeEvery(customizationBaseDuplicated, duplicateImageCustomizationSaga);
}

function* watchResetSingleImageCustomizationSaga() {
    yield* takeEvery(singleCustomizationReset, resetSingleImageCustomizationSaga);
}

function* watchResetAllImageCustomizationsSaga() {
    yield* takeEvery(allModelCustomizationsReset, resetAllImageCustomizationsSaga);
}

function* exportImageCustomizationPdf(appClient: MVisionAppClient) {
    try {
        yield* callApi({
            doApiCall: function* (client) {
                const blob: Blob = yield* call(async () => client.exportCustomizationPdfAsync(appClient, ModelType.Image));
                return blob;
            },
            onFinish: function* (result: Blob | undefined, error) {
                if (error || !result) {
                    const errorMessage = getApiErrorMessage(error, 'Image+ PDF export failed.');
                    yield* put(modelCustomizationExportFailed(errorMessage));
                    yield* put(pdfExportFinished());
                    return false;
                }

                const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
                const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
                const filename = `${EXPORT_IMAGE_JSON_FILE_PREFIX}${isLocalMode ? 'local' : (appClient.userName || 'customizations')}.pdf`;

                downloadBlobToDisk(result, filename);

                yield* put(modelCustomizationExportFailed(null));
                yield* put(pdfExportFinished());
                return true;
            },
        });
    } catch (error) {
        console.error('An error occurred in exportImageCustomizationPdf:', error);
        const errorMessage = getApiErrorMessage(error, 'Image+ PDF export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
        yield* put(pdfExportFinished());
    }
}

export function* watchExportImageCustomizationPdfSaga() {
    while (true) {
        const action = yield* take(modelCustomizationPdfExportRequested);
        yield* put(pdfExportStarted());
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed('PDF export of image customization has failed.'));
            yield* put(pdfExportFinished());
            continue;
        }
        yield* fork(exportImageCustomizationPdf, action.payload);
    }
}

function* watchExportImageCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationExported);
        const errorMessage = 'Export of Image+ customization has failed.';
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed(errorMessage));
            continue;
        }
        yield* fork(exportImageCustomization, action.payload);
    }
}

export function* watchImportImageCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationImported);
        const errorMessage = 'No JSON data provided for Image+ import.';

        if (!action.payload) {
            yield* put(modelCustomizationImportFailed(errorMessage));
            continue;
        }

        yield* fork(importImageCustomization, action.payload);
    }
}


/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForImageConfigSagas() {
    return [
        watchSaveImageCustomizationSaga(),
        watchResetSingleImageCustomizationSaga(),
        watchResetAllImageCustomizationsSaga(),
        watchImageCustomizationDuplicatedSaga(),
        watchExportImageCustomizationSaga(),
        watchExportImageCustomizationPdfSaga(),
        watchImportImageCustomizationSaga(),
    ];
}
